import React, { useContext, useEffect, useReducer, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Image from "react-bootstrap/esm/Image";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import "../CategoryListScreen.css";
import { Store } from "../Store";
import AlertBox from "../components/AlertBox";
import CategoryEditor from "../components/CategoryEditor";
import LoadingBox from "../components/LoadingBox";
import useAxios from "../hooks/useAxios";
import useProductsAndCategories from "../hooks/useProductsAndCategories";
import { getError } from "../utils";

const reducer = (state, action) => {
  switch (action.type) {
    case "DELETE_REQUEST":
      return { ...state, loadingDelete: true, successDelete: false };
    case "DELETE_SUCCESS":
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case "DELETE_FAIL":
      return { ...state, loadingDelete: false, successDelete: false };

    case "DELETE_RESET":
      return { ...state, loadingDelete: false, successDelete: false };

    default:
      return state;
  }
};

export default function CategoryListScreen() {
  const axios = useAxios();

  const [
    loading,
    errorLoading,
    setLoadProducts,
    setLoadCategories,
    setLoadLeafCategories,
  ] = useProductsAndCategories();

  const [{ loadingDelete, successDelete }, dispatch] = useReducer(reducer, {
    loadingDelete: false,
    successDelete: false,
  });

  const { state } = useContext(Store);
  const { userInfo, allCategories: categories } = state;
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const [selectedCategory, setSelectedCategory] = useState("0");
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [rootCategory, setRootCategory] = useState(null);
  const [categoriesFiltered, setCategoriesFiltered] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    if (categories) {
      const categoriesRe = (
        !rootCategory
          ? categories
          : categories.filter((c) => c._id === rootCategory)[0].categories
      ).sort((c1, c2) => new Date(c2.createdAt) - new Date(c1.createdAt));

      setCategoriesFiltered(categoriesRe);
    }
  }, [categories, rootCategory]);

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: "DELETE_RESET" });
    }
  }, [userInfo, successDelete]);

  const confirmDeleteHandler = (category) => {
    setCategoryToDelete(category);
    setShowConfirmModal(true);
  };

  const deleteHandler = async (category) => {
    try {
      dispatch({ type: "DELETE_REQUEST" });
      await axios.delete(`/api/categories/${category._id}`);
      toast.success("Category deleted successfully");

      setLoadProducts(true);
      setLoadCategories(true);
      setLoadLeafCategories(true);
      dispatch({ type: "DELETE_SUCCESS" });
    } catch (err) {
      toast.error(getError(err));
      dispatch({
        type: "DELETE_FAIL",
      });
    }
  };

  return (
    <div className="category-list-grid-container">
      <Helmet>
        <title>Kategoriler</title>
      </Helmet>
      {!isTabletOrMobile && (
        <>
          <h1 className="my-3 text-muted">Kategoriler</h1>
          <hr className="hr"></hr>
        </>
      )}
      <div className="category-list-grid-toolbar">
        <div className="ms-auto">
          <Button
            variant="light"
            className="text-primary border-primary"
            type="button"
            onClick={() => {
              setSelectedCategory("0");
              setShowCategoryEditor(true);
            }}
          >
            <i className="fas fa-plus"></i>
            {!isTabletOrMobile && <>&nbsp;Kategori Oluştur</>}
          </Button>
        </div>
      </div>

      <div className="category-list-grid">
        {categoriesFiltered && categoriesFiltered.length > 0 ? (
          categoriesFiltered.map((category) => (
            <div className="category-list-card" key={category._id}>
              <Image
                className="category-list-image"
                src={category.image}
                alt={category.name}
                fluid
                rounded
                onClick={() => {
                  setSelectedCategory(category._id);
                  setShowCategoryEditor(true);
                }}
              ></Image>
              <h5 className="category-list-name">{category.name}</h5>
              {loadingDelete && category._id === categoryToDelete._id ? (
                <LoadingBox />
              ) : (
                <Button
                  className="delete-button"
                  onClick={() => confirmDeleteHandler(category)}
                >
                  Sil
                </Button>
              )}
            </div>
          ))
        ) : (
          <AlertBox primary>Sisteme kayıtlı kategori bulunamadı</AlertBox>
        )}
      </div>

      <Modal
        backdrop="static"
        show={showCategoryEditor}
        onHide={() => setShowCategoryEditor(false)}
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>
            {selectedCategory === "0" ? "Yeni Kategori" : "Kategori Güncelle"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CategoryEditor
            handleClose={() => setShowCategoryEditor(false)}
            categoryId={selectedCategory}
            setShowCategoryEditor={setShowCategoryEditor}
          ></CategoryEditor>
        </Modal.Body>
      </Modal>

      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Kategoriyi Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Silmek istediğinize emin misiniz?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Vazgeç
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setShowConfirmModal(false);
              deleteHandler(categoryToDelete);
            }}
          >
            Sil
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
